import React from 'react';
import PropTypes from 'prop-types';

import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import InputAdornment from '@mui/material/InputAdornment';
import Grow from '@mui/material/Grow';
import { calculateRgbaWithAlpha } from 'helpers/colorFormatter';
import { OPACITY_73, OPACITY_13, OPACITY_33 } from 'constants/opacity';
import { getItemDescriptionsAndPricesTypography } from 'styles/helpers/getThemeStyles';

const PLACEHOLDER = 'Search menu...';
const CANCEL_BUTTON = 'Cancel';

const SearchBarContainer = styled(Box)(({ theme, isOpen }) => {
  const commonRules = {
    display: 'flex',
    padding: '6px 0 8px',
    borderBottom: `1px solid ${calculateRgbaWithAlpha(
      theme.palette.sectionNav.link.main,
      OPACITY_13
    )}`
  };
  if (isOpen) {
    return {
      ...commonRules,
      width: '100%',
      zIndex: '1',
      backgroundColor: theme.palette.background.default
    };
  }

  return commonRules;
});

const Container = styled(Box)(() => ({
  padding: '2px 4px',
  display: 'flex',
  alignItems: 'center',
  minWidth: '61px',
  width: '100%'
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  height: '33px',
  margin: '0.5px',
  borderColor: calculateRgbaWithAlpha(
    theme.palette.sectionNav.link.main,
    OPACITY_13
  )
}));

const StyledSearchIcon = styled(SearchIcon)(({ theme }) => ({
  fill: calculateRgbaWithAlpha(theme.palette.sectionNav.link.main, OPACITY_73)
}));

const StyledCloseIcon = styled(CancelIcon)(({ theme }) => ({
  fill: calculateRgbaWithAlpha(theme.palette.sectionNav.link.main, OPACITY_33)
}));

const StyledClosedButton = styled(Button)(({ theme }) => {
  return {
    color: calculateRgbaWithAlpha(
      theme.palette.sectionNav.link.main,
      OPACITY_73
    ),
    ...getItemDescriptionsAndPricesTypography(theme),
    fontSize: '14px'
  };
});

const StyledInputBase = styled(InputBase)(({ theme }) => {
  return {
    color: theme.palette.sectionNav.link.main,
    ...getItemDescriptionsAndPricesTypography(theme)
  };
});

const TRANSITION_TIMEOUT_IN_MS = 1000;

const SearchBar = ({
  showSearchInput,
  openInputSearch,
  closeInputSearch,
  searchTerm,
  setSearchTerm
}) => {
  const handleInputChange = event => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  const handleCancelButtonClick = () => {
    setSearchTerm('');
    closeInputSearch();
  };

  return (
    <SearchBarContainer isOpen={showSearchInput} data-testid="menu-search">
      <Container>
        <IconButton
          type="button"
          aria-label="search"
          onClick={openInputSearch}
          style={{ padding: '1rem' }}
        >
          <StyledSearchIcon />
        </IconButton>
        {showSearchInput && (
          <Box sx={{ width: '100%' }}>
            <Grow
              in={showSearchInput}
              style={{ transformOrigin: '0' }}
              {...(showSearchInput
                ? { timeout: TRANSITION_TIMEOUT_IN_MS }
                : {})}
            >
              <Box sx={{ display: 'flex', width: '100%' }}>
                <StyledInputBase
                  sx={{ ml: 1, pr: '10px', flex: 1 }}
                  placeholder={PLACEHOLDER}
                  inputProps={{ 'aria-label': 'search menu' }}
                  value={searchTerm}
                  onChange={handleInputChange}
                  endAdornment={
                    searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          style={{ padding: '1rem' }}
                          type="button"
                          aria-label="clear search bar"
                          onClick={() => setSearchTerm('')}
                          edge="end"
                        >
                          <StyledCloseIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                />
                <StyledDivider orientation="vertical" />
                <StyledClosedButton onClick={handleCancelButtonClick}>
                  {CANCEL_BUTTON}
                </StyledClosedButton>
              </Box>
            </Grow>
          </Box>
        )}
      </Container>
    </SearchBarContainer>
  );
};

SearchBar.propTypes = {
  showSearchInput: PropTypes.bool.isRequired,
  openInputSearch: PropTypes.func.isRequired,
  closeInputSearch: PropTypes.func.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired
};

export default SearchBar;
